// =============================================================================
// NEXUS ENVELOPE HELPERS (WP2) — dependency-free
// =============================================================================
//
// The `@insolitum/types` package is intentionally runtime-dependency-free
// (NO Zod, NO validators). These helpers are therefore hand-rolled. They are
// purely ADDITIVE: existing producers may keep building `NexusEnvelope` objects
// by hand and consuming them unchanged. Nothing here is required on the hot
// path — `wrapEnvelope` is a convenience for new producers, `validateEnvelope`
// a defensive check for boundaries, and `ensureTraceId` a backfill helper.
// =============================================================================

import type { NexusEnvelope, NexusPriority } from './nexus.js';

// -----------------------------------------------------------------------------
// Salience constants — shared heuristic anchors so producers and the encoder
// agree on what "important" means without each re-inventing magic numbers.
// -----------------------------------------------------------------------------

/** Standard salience anchors (0..1). Higher = more important / longer retained. */
export const SALIENCE = {
  /** Routine/background noise; below the encoder's embedding threshold. */
  TRIVIAL: 0.1,
  /** Ordinary domain event; the default when nothing else is known. */
  NORMAL: 0.3,
  /** Embedding threshold — at/above this the encoder will embed when retained. */
  EMBED_THRESHOLD: 0.35,
  /** Noteworthy: failures, breaches, completions. */
  HIGH: 0.6,
  /** Critical: must not be forgotten by ordinary decay. */
  CRITICAL: 0.9,
} as const;

/** Default memory tier applied by the encoder when `memory.retain` is absent. */
export const DEFAULT_RETAIN: NonNullable<NexusEnvelope['memory']>['retain'] = 'episodic';

/** Default perspective applied when `perspective` is absent. */
export const DEFAULT_PERSPECTIVE: NonNullable<NexusEnvelope['perspective']> = 'tenant';

// -----------------------------------------------------------------------------
// trace_id generation — guarded for environments without crypto.randomUUID.
// -----------------------------------------------------------------------------

/**
 * Generate a v4-style UUID. Prefers the platform `crypto.randomUUID()` (Node
 * 16.7+, modern browsers, Deno, edge runtimes) and falls back to a Math.random
 * implementation only where it is unavailable. Dependency-free by design.
 */
export function generateTraceId(): string {
  const c: { randomUUID?: () => string } | undefined =
    typeof globalThis !== 'undefined'
      ? (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
      : undefined;

  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }

  // Fallback: RFC4122-shaped v4 UUID (not cryptographically strong, but fine
  // for trace correlation when no platform RNG is exposed).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Return an envelope guaranteed to carry a `trace_id`. If one is already
 * present (non-empty) the envelope is returned unchanged (same reference);
 * otherwise a shallow copy with a freshly generated `trace_id` is returned.
 */
export function ensureTraceId<T>(env: NexusEnvelope<T>): NexusEnvelope<T> {
  if (env.trace_id && env.trace_id.length > 0) {
    return env;
  }
  return { ...env, trace_id: generateTraceId() };
}

// -----------------------------------------------------------------------------
// wrapEnvelope — fill in sensible defaults for the boilerplate fields so a new
// producer only has to supply the meaningful ones (organization_id, source,
// type, payload, …). Everything stays additive: callers may still override.
// -----------------------------------------------------------------------------

/**
 * Fields a caller MUST supply when wrapping a payload into an envelope.
 * Everything else is defaulted by {@link wrapEnvelope}.
 */
export interface WrapEnvelopeInput<T> {
  organization_id: string;
  source: string;
  type: string;
  payload: T;

  // Optional overrides for the auto-filled fields:
  message_id?: string;
  correlation_id?: string;
  causation_id?: string;
  trace_id?: string;
  destination?: string;
  timestamp?: string;
  schema_version?: string;
  content_type?: string;
  ttl_ms?: number;
  priority?: NexusPriority;
  idempotency_key?: string;

  // Envelope v2 (all optional):
  perspective?: NexusEnvelope<T>['perspective'];
  memory?: NexusEnvelope<T>['memory'];
  entities?: NexusEnvelope<T>['entities'];
  relations?: NexusEnvelope<T>['relations'];
}

/** Current envelope schema version emitted by {@link wrapEnvelope}. */
export const ENVELOPE_SCHEMA_VERSION = '2.0';

/**
 * Build a complete {@link NexusEnvelope} from the meaningful fields, defaulting
 * the rest. `message_id`, `correlation_id` and `trace_id` are generated when
 * not supplied; `timestamp` defaults to now; `schema_version` / `content_type`
 * default to current conventions. Purely additive — hand-built envelopes remain
 * valid and this helper is opt-in for new producers.
 */
export function wrapEnvelope<T>(input: WrapEnvelopeInput<T>): NexusEnvelope<T> {
  const messageId = input.message_id ?? generateTraceId();
  const traceId = input.trace_id && input.trace_id.length > 0 ? input.trace_id : generateTraceId();

  const env: NexusEnvelope<T> = {
    message_id: messageId,
    correlation_id: input.correlation_id ?? messageId,
    trace_id: traceId,
    organization_id: input.organization_id,
    source: input.source,
    timestamp: input.timestamp ?? new Date().toISOString(),
    schema_version: input.schema_version ?? ENVELOPE_SCHEMA_VERSION,
    content_type: input.content_type ?? 'application/json',
    type: input.type,
    payload: input.payload,
  };

  if (input.causation_id !== undefined) env.causation_id = input.causation_id;
  if (input.destination !== undefined) env.destination = input.destination;
  if (input.ttl_ms !== undefined) env.ttl_ms = input.ttl_ms;
  if (input.priority !== undefined) env.priority = input.priority;
  if (input.idempotency_key !== undefined) env.idempotency_key = input.idempotency_key;
  if (input.perspective !== undefined) env.perspective = input.perspective;
  if (input.memory !== undefined) env.memory = input.memory;
  if (input.entities !== undefined) env.entities = input.entities;
  if (input.relations !== undefined) env.relations = input.relations;

  return env;
}

// -----------------------------------------------------------------------------
// validateEnvelope — hand-rolled structural validation (NO Zod). Returns a
// list of human-readable errors instead of throwing, so callers at a boundary
// can decide what to do.
// -----------------------------------------------------------------------------

const PERSPECTIVES = ['tenant', 'partner', 'super_admin'];
const RETAIN_TIERS = ['none', 'sensory', 'episodic', 'semantic'];
const PRIORITIES = ['critical', 'high', 'normal', 'low'];

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isNumberInRange(v: unknown, min: number, max: number): boolean {
  return typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max;
}

/**
 * Structurally validate a (possibly partial/untrusted) envelope. Checks the
 * required string fields, that `payload` exists, and — when present — that the
 * optional v2 fields are well-formed. Does NOT mutate. Returns
 * `{ ok, errors }`; `ok` is `true` iff `errors` is empty.
 */
export function validateEnvelope(env: unknown): { ok: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!isPlainObject(env)) {
    return { ok: false, errors: ['envelope must be an object'] };
  }

  const requiredStrings = [
    'message_id',
    'correlation_id',
    'trace_id',
    'organization_id',
    'source',
    'timestamp',
    'schema_version',
    'content_type',
    'type',
  ] as const;
  for (const key of requiredStrings) {
    if (!isNonEmptyString(env[key])) {
      errors.push(`${key} is required and must be a non-empty string`);
    }
  }

  if (!('payload' in env)) {
    errors.push('payload is required');
  }

  // Optional v2 / existing optional fields — only validated when present.
  if (env.perspective !== undefined && !PERSPECTIVES.includes(env.perspective as string)) {
    errors.push(`perspective must be one of ${PERSPECTIVES.join(', ')}`);
  }

  if (env.priority !== undefined && !PRIORITIES.includes(env.priority as string)) {
    errors.push(`priority must be one of ${PRIORITIES.join(', ')}`);
  }

  if (env.ttl_ms !== undefined && !(typeof env.ttl_ms === 'number' && env.ttl_ms >= 0)) {
    errors.push('ttl_ms must be a non-negative number');
  }

  if (env.memory !== undefined) {
    if (!isPlainObject(env.memory)) {
      errors.push('memory must be an object');
    } else {
      const m = env.memory;
      if (m.salience !== undefined && !isNumberInRange(m.salience, 0, 1)) {
        errors.push('memory.salience must be a number between 0 and 1');
      }
      if (m.retain !== undefined && !RETAIN_TIERS.includes(m.retain as string)) {
        errors.push(`memory.retain must be one of ${RETAIN_TIERS.join(', ')}`);
      }
      if (
        m.ttl_days !== undefined &&
        !(typeof m.ttl_days === 'number' && Number.isFinite(m.ttl_days) && m.ttl_days >= 0)
      ) {
        errors.push('memory.ttl_days must be a non-negative number');
      }
      if (m.summary !== undefined && typeof m.summary !== 'string') {
        errors.push('memory.summary must be a string');
      }
    }
  }

  if (env.entities !== undefined) {
    if (!Array.isArray(env.entities)) {
      errors.push('entities must be an array');
    } else {
      env.entities.forEach((e, i) => {
        if (!isPlainObject(e)) {
          errors.push(`entities[${i}] must be an object`);
          return;
        }
        if (!isNonEmptyString(e.ref)) {
          errors.push(`entities[${i}].ref is required and must be a non-empty string`);
        }
        if (!isNonEmptyString(e.node_type)) {
          errors.push(`entities[${i}].node_type is required and must be a non-empty string`);
        } else if (!(e.node_type as string).includes('.')) {
          errors.push(`entities[${i}].node_type must be namespaced '<slug>.<entity>'`);
        }
        if (e.label !== undefined && typeof e.label !== 'string') {
          errors.push(`entities[${i}].label must be a string`);
        }
        if (e.props !== undefined && !isPlainObject(e.props)) {
          errors.push(`entities[${i}].props must be an object`);
        }
        if (e.salience !== undefined && !isNumberInRange(e.salience, 0, 1)) {
          errors.push(`entities[${i}].salience must be a number between 0 and 1`);
        }
      });
    }
  }

  if (env.relations !== undefined) {
    if (!Array.isArray(env.relations)) {
      errors.push('relations must be an array');
    } else {
      env.relations.forEach((r, i) => {
        if (!isPlainObject(r)) {
          errors.push(`relations[${i}] must be an object`);
          return;
        }
        if (!isNonEmptyString(r.src)) {
          errors.push(`relations[${i}].src is required and must be a non-empty string`);
        }
        if (!isNonEmptyString(r.rel)) {
          errors.push(`relations[${i}].rel is required and must be a non-empty string`);
        }
        if (!isNonEmptyString(r.dst)) {
          errors.push(`relations[${i}].dst is required and must be a non-empty string`);
        }
        if (r.weight !== undefined && !isNumberInRange(r.weight, 0, 1)) {
          errors.push(`relations[${i}].weight must be a number between 0 and 1`);
        }
      });
    }
  }

  return { ok: errors.length === 0, errors };
}

// -----------------------------------------------------------------------------
// readEventPayload — transitional shim for consumers during the Envelope v2
// rollout. A decoded NATS message may be EITHER a bare legacy payload (older
// producers publish domain fields at the top level) OR an Envelope v2 (domain
// data nested under `payload`). Consumers that historically read bare top-level
// fields would silently see `undefined` once a producer switches to v2.
//
// This returns the domain data as a flat object regardless of shape: for an
// envelope, the envelope-level fields are merged with `payload` (payload wins),
// so a consumer keeps reading `data.order_id` while also gaining envelope
// context (`organization_id`, `trace_id`, …); for a bare payload it is returned
// as-is. Envelope detection is conservative: an object with a plain-object
// `payload` AND at least one envelope marker (`schema_version` / `message_id`).
// -----------------------------------------------------------------------------

/**
 * Envelope-aware read of a decoded NATS message's domain data (see above).
 * Generic in the returned shape so typed consumers (whose handlers historically
 * received `any` from `JSON.parse`) keep their contextual types via return-type
 * inference; defaults to `Record<string, unknown>` for untyped readers.
 */
export function readEventPayload<T = Record<string, unknown>>(decoded: unknown): T {
  if (!isPlainObject(decoded)) return {} as T;
  const hasEnvelopeMarker = 'schema_version' in decoded || 'message_id' in decoded;
  if (hasEnvelopeMarker && isPlainObject(decoded.payload)) {
    return { ...decoded, ...(decoded.payload as Record<string, unknown>) } as T;
  }
  return decoded as T;
}
